import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';
import { Line } from 'react-chartjs-2';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';
import NextAction from '../Widget/NextAction';

import ShareDrawer from '../../components/ShareDrawer';
import CssTransition from '../../components/Transition/CssTransition';
import EmailInterstitial from '../../components/EmailInterstitial/EmailInterstitialPopup';
import countdownAnimation from '../../components/Widget/CountdownAnimation.json';

import {
    Footer,
    Title as QuestionText,
    PeopleSigned as PeopleAnswered
} from '../DefaultStyles/Shared';
import { Container, Body } from '../DefaultStyles/Details';

import {
    Arrows,
    Content,
    Details,
    Feedback,
    GraphContainer,
    SentimentLabel,
    SentimentLabelContainer,
    Slider,
    SliderContainer,
    SliderLogo,
    SliderSegment,
    SliderSegmentContainer,
    SliderThumb
} from '../../components/SentimentPoll/Components';

import {
    onBackWithinCard,
    onShowEmailInterstitial,
    setOverlayPartnerVisible,
    widgetEngaged
} from '../Widget/actions';
import {
    onPollAnswered,
    onInteractWithSlider,
    sentimentPollAnswered,
    setPresetDistributionData
} from './actions';

import { CardTypes, ActionTypes } from '../../utils/enums';
import { formatEmoji } from '../../utils/fillDynamicText';
import { getActionCompletedTime } from '../../utils/actionTiming';
import { isLongText } from '../../components/Shared/commonFunctions';
import { isPortrait } from '../../utils/size';
import { sumDistributionRange, getFeedbackText } from '../../utils/sentimentPoll/helpers';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { copyFor, capitalization } from '../../copy/copy';

let lottie = {};
const lottiePromise = import('lottie-web').then(l => (lottie = l.default));

class SentimentPollDetails extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hover: false,
            isPaused: false,
            sliderPosition: 50,
            positionChanged: false,
            touchTimer: null
        };
    }

    static propTypes = {
        actionCompletedTime: PropTypes.func.isRequired,
        actionIdentifier: PropTypes.string.isRequired,
        actionInitiated: PropTypes.bool.isRequired,
        activeAction: PropTypes.object,
        answerId: PropTypes.number,
        apiRequestData: PropTypes.object.isRequired,
        card: PropTypes.object.isRequired,
        customLogoId: PropTypes.string,
        customRadiatingColor: PropTypes.string,
        emailInterstitialVisibility: PropTypes.bool.isRequired,
        engaged: PropTypes.bool,
        hasNextAction: PropTypes.bool.isRequired,
        isMultiActionsCard: PropTypes.bool.isRequired,
        leftPercentage: PropTypes.number,
        noOfAnswers: PropTypes.string.isRequired,
        onBackWithinCard: PropTypes.func.isRequired,
        onInteractWithSlider: PropTypes.func.isRequired,
        onPollAnswered: PropTypes.func.isRequired,
        onShowEmailInterstitial: PropTypes.func.isRequired,
        pollAnswered: PropTypes.bool.isRequired,
        questionText: PropTypes.string,
        resultDistribution: PropTypes.arrayOf(PropTypes.number).isRequired,
        rightPercentage: PropTypes.number,
        sentimentPollAnswered: PropTypes.func.isRequired,
        setOverlayPartnerVisible: PropTypes.func.isRequired,
        setPresetDistributionData: PropTypes.func.isRequired,
        showEmailInterstitial: PropTypes.bool.isRequired,
        widgetEngaged: PropTypes.func.isRequired
    };

    componentDidMount() {
        lottiePromise.then(() => this.lottieCallback());
    }

    onAnswerSentimentPoll = sentimentValue => {
        if (this.props.pollAnswered) {
            this.props.onPollAnswered(
                this.props.apiRequestData,
                this.props.actionIdentifier,
                this.props.actionCompletedTime(),
                this.props.questionText,
                sentimentValue / 100,
                this.props.isMultiActionsCard,
                this.props.answerId,
                this.props.pollAnswered,
                getFeedbackText(
                    this.props.activeAction,
                    this.props.resultDistribution,
                    sentimentValue / 100
                ),
                this.props.noOfAnswers + 1
            );
        } else {
            if (this.props.showEmailInterstitial) {
                this.props.onShowEmailInterstitial();
            } else {
                this.props.sentimentPollAnswered();
            }
            this.props.setPresetDistributionData(
                this.props.resultDistribution,
                sentimentValue / 100
            );
            this.props.onPollAnswered(
                this.props.apiRequestData,
                this.props.actionIdentifier,
                this.props.actionCompletedTime(),
                this.props.questionText,
                sentimentValue / 100,
                this.props.isMultiActionsCard,
                null,
                this.props.pollAnswered,
                getFeedbackText(
                    this.props.activeAction,
                    this.props.resultDistribution,
                    sentimentValue / 100
                ),
                this.props.noOfAnswers + 1
            );
        }
    };

    handleHover(bool) {
        this.setState({ hover: bool });
    }

    lottieCallback() {
        if (lottie && this.sliderRef) {
            lottie.loadAnimation({
                container: this.sliderRef,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: countdownAnimation,
                progressiveLoad: false
            });
        }
    }

    animateLottie(bool) {
        if (bool) this.lottieCallback();
    }

    handleSliderTouchStart() {
        this.setState({ hover: false, startTime: Date.now(), isPaused: true });
        this.animateLottie(false);
        this.props.widgetEngaged(this.props.engaged);
    }

    handleSliderTouchEnd(e) {
        const requiredTimePassed = Date.now() - this.state.startTime > 300;
        if (requiredTimePassed || this.state.positionChanged) {
            this.props.onInteractWithSlider(
                this.props.actionInitiated,
                this.props.actionIdentifier
            );
            this.onAnswerSentimentPoll(e.target.value);
        }
    }

    handleSliderPositionChange(e) {
        this.setState({
            positionChanged: true,
            sliderPosition: e.target.value
        });
    }

    componentWillUnmount() {
        if (lottie) {
            lottie.destroy();
        }
    }

    render() {
        const chartData = {
            labels: new Array((this.props.resultDistribution || []).length).fill(''),
            datasets: [
                {
                    backgroundColor: 'rgba(255, 36, 94, 0.2)',
                    borderCapStyle: 'round',
                    borderColor: 'rgba(255, 36, 94, 0)',
                    borderWidth: 0,
                    fill: true,
                    lineTension: 0.25,
                    pointBackgroundColor: '#fff',
                    pointBorderWidth: 3,
                    pointHitRadius: 10,
                    pointHoverRadius: 0,
                    pointRadius: 0,
                    data: this.props.resultDistribution
                }
            ]
        };
        const chartOptions = {
            tooltips: {
                enabled: false
            },
            legend: {
                display: false
            },
            layout: {
                padding: 0
            },
            responsive: true,
            animation: {
                duration: 800,
                delay: 1200,
                easing: 'easeInOutQuint'
            },
            scales: {
                xAxes: [
                    {
                        display: false
                    }
                ],
                yAxes: [
                    {
                        display: false,
                        ticks: {
                            beginAtZero: true
                        }
                    }
                ]
            }
        };
        return (
            <Container>
                <Overlay />
                <OverlayPublisher transitionType="right-to-left" />
                <OverlayPartner transitionType="right-to-left" />
                <Body>
                    <WidgetHeader />
                    <Content isSentimentPoll={true}>
                        <QuestionText
                            isLongText={isLongText(this.props.questionText, 40)}
                            isExtraLongText={isLongText(this.props.questionText, 60)}
                            dangerouslySetInnerHTML={{
                                __html: this.props.questionText
                            }}
                            onClick={e => {
                                e.preventDefault();
                                this.props.setOverlayPartnerVisible(true, e, false);
                            }}
                        />
                        {!this.props.pollAnswered ? (
                            <PeopleAnswered>
                                {this.props.noOfAnswers}{' '}
                                {copyFor(
                                    'miscellaneous',
                                    'numAnswered',
                                    'sentimentpoll',
                                    capitalization.all
                                )}
                            </PeopleAnswered>
                        ) : (
                            <Feedback pollAnswered={this.props.pollAnswered}>
                                {this.props.noOfAnswers}{' '}
                                {copyFor('miscellaneous', 'numAnswered', 'sentimentpoll')}
                                {'. '}
                                {getFeedbackText(
                                    this.props.activeAction,
                                    this.props.resultDistribution,
                                    this.state.sliderPosition / 100
                                )}
                            </Feedback>
                        )}
                        <Details
                            pollAnswered={this.props.pollAnswered}
                            hasNextAction={this.props.hasNextAction}
                            onMouseEnter={() => this.handleHover(true)}
                            onMouseLeave={() => this.handleHover(false)}
                        >
                            <GraphContainer
                                hasNextAction={this.props.hasNextAction}
                                maxSize={
                                    isPortrait()
                                        ? this.props.hasNextAction
                                            ? 0.75
                                            : 0.85
                                        : this.props.hasNextAction
                                            ? 0.5 // eslint-disable-line
                                            : 0.65 // eslint-disable-line
                                }
                                pollAnswered={this.props.pollAnswered}
                            >
                                {this.props.pollAnswered && (
                                    <Line options={chartOptions} data={chartData} />
                                )}
                            </GraphContainer>
                            <SliderContainer pollAnswered={this.props.pollAnswered}>
                                <SliderSegmentContainer
                                    pollAnswered={this.props.pollAnswered}
                                >
                                    <SliderSegment width={50} />
                                    <SliderSegment width={50} />
                                </SliderSegmentContainer>
                                <Slider
                                    pollAnswered={this.props.pollAnswered}
                                    isPaused={this.state.isPaused}
                                    onChange={e => this.handleSliderPositionChange(e)}
                                    onTouchStart={() => this.handleSliderTouchStart()}
                                    onMouseDown={() => this.handleSliderTouchStart()}
                                    onTouchEnd={e => this.handleSliderTouchEnd(e)}
                                    onMouseUp={e => this.handleSliderTouchEnd(e)}
                                    type="range"
                                />
                                <Arrows
                                    isPaused={this.state.isPaused}
                                    hover={this.state.hover}
                                    pollAnswered={this.props.pollAnswered}
                                    sliderPosition={this.state.sliderPosition}
                                />
                                <SliderThumb
                                    pollAnswered={this.props.pollAnswered}
                                    isPaused={this.state.isPaused}
                                    position={this.state.sliderPosition}
                                    customRadiatingColor={this.props.customRadiatingColor}
                                    innerRef={i => (this.sliderRef = i)}
                                >
                                    <SliderLogo
                                        customLogoId={this.props.customLogoId}
                                        isPaused={this.state.isPaused}
                                        pollAnswered={this.props.pollAnswered}
                                    />
                                </SliderThumb>
                            </SliderContainer>
                            <SentimentLabelContainer>
                                <SentimentLabel
                                    pollAnswered={this.props.pollAnswered}
                                    hasNextAction={this.props.hasNextAction}
                                >
                                    <h5>
                                        {this.props.pollAnswered &&
                                            `${this.props.leftPercentage}%`}
                                    </h5>
                                    <h4
                                        dangerouslySetInnerHTML={{
                                            __html: formatEmoji(
                                                this.props.activeAction.leftChoice
                                            )
                                        }}
                                    />
                                </SentimentLabel>
                                <SentimentLabel
                                    pollAnswered={this.props.pollAnswered}
                                    hasNextAction={this.props.hasNextAction}
                                >
                                    <h5>
                                        {this.props.pollAnswered &&
                                            `${this.props.rightPercentage}%`}
                                    </h5>
                                    <h4
                                        dangerouslySetInnerHTML={{
                                            __html: formatEmoji(
                                                this.props.activeAction.rightChoice
                                            )
                                        }}
                                    />
                                </SentimentLabel>
                            </SentimentLabelContainer>
                        </Details>
                    </Content>
                    {this.props.pollAnswered &&
                        this.props.hasNextAction &&
                        !this.props.emailInterstitialVisibility && <NextAction />}
                    <Footer>
                        <ShareDrawer
                            goBack={() =>
                                this.props.card.type === CardTypes.actionChoice
                                    ? this.props.onBackWithinCard(
                                          this.props.actionIdentifier,
                                          this.props.card,
                                          true
                                      )
                                    : null
                            }
                            fullwidth={true}
                            newsletterSignUpVisible={
                                this.props.card.type !== CardTypes.actionChoice
                            }
                            hasBackButton={
                                this.props.card.type === CardTypes.actionChoice
                            }
                            backgroundColor={this.props.pollAnswered ? null : '#ffffff'}
                        />
                    </Footer>
                </Body>
                <TransitionGroup>
                    {this.props.emailInterstitialVisibility && (
                        <CssTransition
                            key="alert"
                            in={this.props.onShowEmailInterstitial}
                        >
                            <EmailInterstitial skipSuccessNavigation={true} />
                        </CssTransition>
                    )}
                </TransitionGroup>
            </Container>
        );
    }
}

export default connect(
    state => {
        const hasNextAction =
            getNextAction(
                state.widget.actions,
                state.widget.activeAction.identifier,
                buildConditionalActionsAnswerHistory(state),
                state.widget.cards
            ) !== null;

        return {
            actionCompletedTime: getActionCompletedTime(state, 'sentimentPoll'),
            actionIdentifier: state.widget.activeAction.identifier,
            actionInitiated: state.sentimentPoll.actionInitiated,
            activeAction: state.widget.activeAction,
            answerId: state.sentimentPoll.pollAnswered
                ? state.sentimentPoll.answerId
                : null,
            apiRequestData: state.widget.apiRequestData,
            customLogoId: state.widget.activeAction.customLogoId,
            customRadiatingColor: state.widget.activeAction.customRadiatingColor,
            card: state.widget.cards[state.widget.activeAction.order - 1],
            emailInterstitialVisibility: state.widget.showEmailInterstitial || false,
            engaged: state.widget.engaged,
            hasNextAction,
            isLastActionInWidget:
                state.widget.cards.length === state.widget.activeAction.order,
            isMultiActionsCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.multipleActions,
            leftPercentage:
                state.sentimentPoll.resultDistribution.length > 0
                    ? Math.round(
                          (sumDistributionRange(
                              state.sentimentPoll.resultDistribution,
                              0,
                              6
                          ) /
                              sumDistributionRange(
                                  state.sentimentPoll.resultDistribution,
                                  0,
                                  12
                              )) *
                              100
                      )
                    : 0,
            noOfAnswers: (state.widget.activeAction.noOfAnswers || 53).toLocaleString(),
            pollAnswered: state.sentimentPoll.pollAnswered,
            questionText:
                state.widget.cards[state.widget.activeAction.order - 1].heading ||
                state.widget.activeAction.name,
            resultDistribution:
                state.sentimentPoll.resultDistribution.length > 0
                    ? state.sentimentPoll.resultDistribution
                    : state.widget.activeAction.resultDistribution,
            rightPercentage:
                state.sentimentPoll.resultDistribution.length > 0
                    ? Math.round(
                          (sumDistributionRange(
                              state.sentimentPoll.resultDistribution,
                              6,
                              12
                          ) /
                              sumDistributionRange(
                                  state.sentimentPoll.resultDistribution,
                                  0,
                                  12
                              )) *
                              100
                      )
                    : 0,
            showEmailInterstitial: state.widget.activeAction.collectEmail > 0,
            widgetId: state.widget.widgetRequestId
        };
    },
    {
        onBackWithinCard,
        onInteractWithSlider,
        onPollAnswered,
        onShowEmailInterstitial,
        sentimentPollAnswered,
        setOverlayPartnerVisible,
        setPresetDistributionData,
        widgetEngaged
    },
    null,
    {
        areStatesEqual: (next, prev) => {
            if (next.widget.activeAction.type !== ActionTypes.SentimentPoll) return true;

            return next === prev;
        }
    }
)(SentimentPollDetails);
